import { serverSetAccessToken } from "@/api";
import { useToast } from "@/hooks";
import { featuresService } from "@/services/featuresService";
import { consumeLoginRedirect } from "@/utils/loginRedirect";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { authService } from "../authService";
import type { AuthResponse } from "../authType";

type ResetPasswordParams = {
  phone: string;
  code: string;
};

/**
 * Redefine a senha e já autentica o usuário com os tokens retornados,
 * levando direto para a área logada. `phone` e `code` vêm da query das
 * etapas anteriores.
 */
export const useResetPassword = ({ phone, code }: ResetPasswordParams) => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { mutate: resetPassword, isPending: isPendingResetPassword } =
    useMutation<AuthResponse, Error, string>({
      mutationFn: newPassword => {
        if (!phone || !code) {
          throw new Error("Telefone e código são obrigatórios");
        }

        return authService.handleResetPassword({ phone, code, newPassword });
      },
      onSuccess: async data => {
        await serverSetAccessToken(
          data.accessToken,
          data.refreshToken,
          data.session
        );
        if (data.features) {
          featuresService.setFeatures(data.features);
        }
        showToast("Senha redefinida com sucesso", "success");
        navigate(consumeLoginRedirect() ?? "/dashboard");
      },
      onError: error => {
        showToast(error.message, "error");
      },
    });

  return {
    resetPassword,
    isPendingResetPassword,
  };
};
